import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, Building2, Database, CheckCircle2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import TenantAdminAccountForm from '../components/TenantAdminAccountForm';

const PLANS = [
  { id: 'starter', label: 'Starter', desc: 'Up to 10 users, 1 GB storage', users: 10, storage: 1024 },
  { id: 'professional', label: 'Professional', desc: 'Up to 50 users, 10 GB storage', users: 50, storage: 10240 },
  { id: 'enterprise', label: 'Enterprise', desc: 'Unlimited users, 100 GB storage', users: 999, storage: 102400 },
];

const CreateTenant = ({ mode = 'create' }) => {
  const viewMode = mode === 'view';
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(viewMode);
  const [step, setStep] = useState(1);
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({
    name: '', slug: '', plan: 'starter',
    contactEmail: '', contactPhone: '', address: '',
    maxUsers: 10, maxStorageMb: 1024,
    adminName: '', adminEmail: '', adminPassword: 'Admin@123456',
    newPassword: '', confirmPassword: ''
  });

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  useEffect(() => {
    if (!viewMode || !id) return;
    let mounted = true;

    const fetchTenant = async () => {
      setPageLoading(true);
      try {
        const res = await api.get(`/super-admin/tenants/${id}`);
        if (!mounted) return;
        const t = res.data.tenant;
        setTenant(t);
        setForm(f => ({
          ...f,
          name: t.name || '',
          slug: t.slug || '',
          plan: t.plan || 'starter',
          contactEmail: t.contact_email || '',
          contactPhone: t.contact_phone || '',
          address: t.address || '',
          maxUsers: t.max_users ?? 10,
          maxStorageMb: t.max_storage_mb ?? 1024,
          adminName: t.admin?.name || '',
          adminEmail: t.admin?.email || '',
          adminPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      } catch (err) {
        if (mounted) setTenant(null);
      } finally {
        if (mounted) setPageLoading(false);
      }
    };

    fetchTenant();
    return () => { mounted = false; };
  }, [id, viewMode]);

  const autoSlug = (name) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    updateField('slug', slug);
    updateField('name', name);
  };

  const selectPlan = (plan) => {
    const p = PLANS.find(p => p.id === plan);
    setForm(f => ({ ...f, plan, maxUsers: p.users, maxStorageMb: p.storage }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.contactEmail) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/super-admin/tenants', form);
      toast.success('Tenant onboarded successfully!');
      navigate('/super-admin/tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!form.newPassword || !form.confirmPassword) {
      toast.error('Please fill both password fields');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/super-admin/tenants/${id}/admin-password`, {
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword,
      });
      toast.success('Tenant admin password updated');
      updateField('newPassword', '');
      updateField('confirmPassword', '');
    } finally {
      setLoading(false);
    }
  };

  if (viewMode && pageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (viewMode && !tenant) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/super-admin/tenants')} className="btn-secondary !py-1.5 !px-3">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="card">
          <h1 className="text-xl font-semibold text-gray-900">Tenant not found</h1>
          <p className="text-sm text-gray-500 mt-1">The selected tenant could not be loaded.</p>
        </div>
      </div>
    );
  }

  const adminCard = (
    <TenantAdminAccountForm
      mode={viewMode ? 'view' : 'create'}
      tenant={{
        name: form.name,
        slug: form.slug,
        plan: form.plan,
        contactEmail: form.contactEmail,
        maxUsers: form.maxUsers,
        maxStorageMb: form.maxStorageMb,
      }}
      adminName={form.adminName}
      adminEmail={viewMode ? form.adminEmail : form.contactEmail}
      adminPassword={form.adminPassword}
      newPassword={form.newPassword}
      confirmPassword={form.confirmPassword}
      loading={loading}
      onBack={() => (viewMode ? navigate('/super-admin/tenants') : setStep(2))}
      onAdminNameChange={value => updateField('adminName', value)}
      onAdminPasswordChange={value => updateField('adminPassword', value)}
      onNewPasswordChange={value => updateField('newPassword', value)}
      onConfirmPasswordChange={value => updateField('confirmPassword', value)}
      submitLabel={viewMode ? 'Update Password' : (loading ? 'Creating...' : '✓ Create Tenant')}
    />
  );

  if (viewMode) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/super-admin/tenants')} className="btn-secondary !py-1.5 !px-3">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tenant Admin Account</h1>
            <p className="text-gray-500 text-sm">View tenant details and reset the tenant admin password</p>
          </div>
        </div>

        <form onSubmit={handleResetSubmit}>
          {adminCard}
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/super-admin/tenants" className="btn-secondary !py-1.5 !px-3">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onboard New Tenant</h1>
          <p className="text-gray-500 text-sm">Create a new organization with its own isolated workspace</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {[1, 2, 3].map(s => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              step === s ? 'bg-primary-100 text-primary-700' :
              step > s ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {step > s ? <CheckCircle2 className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center text-xs">{s}</span>}
              {['Organization Info', 'Choose Plan', 'Admin Account'][s - 1]}
            </div>
            {s < 3 && <div className={`flex-1 h-px ${step > s ? 'bg-green-300' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleCreateSubmit}>
        {step === 1 && (
          <div className="card space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold">Organization Details</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="label">Organization Name *</label>
                <input className="input" placeholder="Acme Corporation" value={form.name}
                  onChange={e => autoSlug(e.target.value)} required />
              </div>
              <div className="col-span-2">
                <label className="label">Organization Slug (URL ID) *</label>
                <div className="flex items-center">
                  <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 rounded-l-lg text-sm text-gray-500">dms.app/</span>
                  <input className="input rounded-l-none" placeholder="acme-corp" value={form.slug}
                    onChange={e => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} required />
                </div>
                <p className="text-xs text-gray-400 mt-1">Used for tenant login. Lowercase letters, numbers, hyphens only.</p>
              </div>
              <div className="col-span-2">
                <label className="label">Contact Email *</label>
                <input type="email" className="input" placeholder="admin@acme.com" value={form.contactEmail}
                  onChange={e => updateField('contactEmail', e.target.value)} required />
              </div>
              <div>
                <label className="label">Contact Phone</label>
                <input className="input" placeholder="+91 98765 43210" value={form.contactPhone}
                  onChange={e => updateField('contactPhone', e.target.value)} />
              </div>
              <div>
                <label className="label">Address</label>
                <input className="input" placeholder="City, Country" value={form.address}
                  onChange={e => updateField('address', e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button type="button" onClick={() => setStep(2)} className="btn-primary">Next: Choose Plan -></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="card space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <Database className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold">Select Plan</h2>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {PLANS.map(p => (
                <label key={p.id} className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  form.plan === p.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input type="radio" name="plan" value={p.id} checked={form.plan === p.id}
                    onChange={() => selectPlan(p.id)} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{p.label}</span>
                      {p.id === 'professional' && <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">Popular</span>}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <label className="label">Max Users</label>
                <input type="number" className="input" value={form.maxUsers}
                  onChange={e => updateField('maxUsers', parseInt(e.target.value))} min={1} />
              </div>
              <div>
                <label className="label">Max Storage (MB)</label>
                <input type="number" className="input" value={form.maxStorageMb}
                  onChange={e => updateField('maxStorageMb', parseInt(e.target.value))} min={100} />
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <button type="button" onClick={() => setStep(1)} className="btn-secondary">{'<- Back'}</button>
              <button type="button" onClick={() => setStep(3)} className="btn-primary">Next: Admin Account -></button>
            </div>
          </div>
        )}

        {step === 3 && adminCard}
      </form>
    </div>
  );
};

export default CreateTenant;
